import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { Search, RefreshCw, ChevronRight, Monitor, Cpu, Shield, LayoutGrid, List, MapPin, User, Upload, Download, X, Check, AlertTriangle } from 'lucide-react';
import './Assets.css';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');

// ─── CSV Template ────────────────────────────────────────────────────────────
const CSV_TEMPLATE = `nombre,tipo_dispositivo,fabricante,modelo,numero_serie,usuario,ip,mac,ubicacion,notas
PC-VENTAS-01,workstation,Dell,OptiPlex 7090,ABC123,Juan Pérez,192.168.1.10,AA:BB:CC:DD:EE:FF,Oficina Central,
LAPTOP-RRHH-02,laptop,Lenovo,ThinkPad X1,XYZ789,María García,192.168.1.11,,Piso 2,
SRV-FILE-01,server,HP,ProLiant DL380,SRV001,,192.168.0.1,,Datacenter,Servidor de archivos`;

function downloadTemplate() {
  const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = 'plantilla-activos.csv'; a.click();
  URL.revokeObjectURL(url);
}

// ─── CSV Parser ───────────────────────────────────────────────────────────────
function parseCSV(text) {
  const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n').filter(l => l.trim());
  if (lines.length < 2) return { headers: [], rows: [] };

  const parseRow = (line) => {
    const result = [];
    let cur = '', inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { inQuotes = !inQuotes; }
      else if (ch === ',' && !inQuotes) { result.push(cur.trim()); cur = ''; }
      else { cur += ch; }
    }
    result.push(cur.trim());
    return result;
  };

  const headers = parseRow(lines[0]);
  const rows = lines.slice(1).map(line => {
    const vals = parseRow(line);
    const obj = {};
    headers.forEach((h, i) => { obj[h] = vals[i] || ''; });
    return obj;
  });
  return { headers, rows };
}

// ─── Modal de Importación ─────────────────────────────────────────────────────
function ImportModal({ onClose, onImported }) {
  const fileRef = useRef();
  const [step, setStep] = useState('upload'); // upload | preview | importing | done
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleFile = (file) => {
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const { headers: h, rows: r } = parseCSV(e.target.result);
      if (!h.length || !r.length) { setError('El archivo está vacío o tiene formato incorrecto.'); return; }
      setError('');
      setHeaders(h);
      setRows(r);
      setStep('preview');
    };
    reader.readAsText(file, 'UTF-8');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file?.name.endsWith('.csv')) handleFile(file);
  };

  const doImport = async () => {
    setStep('importing');
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/assets/import/csv`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ rows }),
      });
      const data = await r.json();
      if (!r.ok) { setError(data.error || 'Error al importar'); setStep('preview'); return; }
      setResult(data);
      setStep('done');
      onImported();
    } catch (err) {
      setError('Error de red al importar.');
      setStep('preview');
    }
  };

  const DEVICE_TYPES = ['workstation','laptop','server','printer','switch','router','access_point','ups','phone','camera','scanner','other'];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: 'var(--surface)', borderRadius: 14, width: '100%', maxWidth: 820, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Upload size={18} style={{ color: 'var(--accent)' }} /> Importar activos desde CSV
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 2 }}>
              {step === 'upload' && 'Seleccioná o arrastrá tu archivo CSV'}
              {step === 'preview' && `${rows.length} filas encontradas en "${fileName}"`}
              {step === 'importing' && 'Importando...'}
              {step === 'done' && 'Importación completada'}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-2)', padding: 4 }}>
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
          {/* PASO 1: Upload */}
          {step === 'upload' && (
            <div>
              <div
                onDrop={handleDrop}
                onDragOver={e => e.preventDefault()}
                onClick={() => fileRef.current.click()}
                style={{ border: '2px dashed #93c5fd', borderRadius: 12, padding: 48, textAlign: 'center', cursor: 'pointer', background: '#f0f9ff', transition: 'border-color 0.2s' }}
              >
                <Upload size={36} style={{ color: 'var(--accent)', marginBottom: 12 }} />
                <div style={{ fontWeight: 600, color: '#1e40af', marginBottom: 6 }}>Hacé click o arrastrá tu CSV aquí</div>
                <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>Solo archivos .csv con codificación UTF-8</div>
                <input ref={fileRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0])} />
              </div>
              {error && <div style={{ color: 'var(--red)', fontSize: 13, marginTop: 10 }}>{error}</div>}

              {/* Template */}
              <div style={{ marginTop: 20, padding: 16, background: 'var(--bg-subtle)', borderRadius: 10, border: '1px solid var(--border)' }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10 }}>📋 Columnas del CSV</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
                  {[
                    { col: 'nombre', req: true, desc: 'Hostname del equipo' },
                    { col: 'tipo_dispositivo', req: false, desc: 'workstation, laptop, server…' },
                    { col: 'fabricante', req: false, desc: 'Dell, HP, Lenovo…' },
                    { col: 'modelo', req: false, desc: 'Modelo del equipo' },
                    { col: 'numero_serie', req: false, desc: 'Número de serie' },
                    { col: 'usuario', req: false, desc: 'Usuario asignado' },
                    { col: 'ip', req: false, desc: 'Dirección IP' },
                    { col: 'mac', req: false, desc: 'Dirección MAC' },
                    { col: 'ubicacion', req: false, desc: 'Nombre de ubicación' },
                    { col: 'notas', req: false, desc: 'Notas libres' },
                  ].map(({ col, req, desc }) => (
                    <div key={col} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 7, padding: '8px 10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <code style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 600 }}>{col}</code>
                        {req && <span style={{ fontSize: 9, color: 'var(--red)', fontWeight: 600 }}>REQ</span>}
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--ink-3)', marginTop: 2 }}>{desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* PASO 2: Preview */}
          {step === 'preview' && (
            <div>
              {error && (
                <div style={{ background: 'var(--red-dim)', border: '1px solid var(--red-dim)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, color: 'var(--red)', fontSize: 13 }}>
                  {error}
                </div>
              )}
              <div style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 12 }}>
                Vista previa de las primeras {Math.min(rows.length, 10)} de <strong>{rows.length}</strong> filas. Si ya existe un equipo con ese nombre, se <strong>actualizará</strong>.
              </div>
              <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-subtle)' }}>
                      <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid var(--border)', fontWeight: 600, color: 'var(--ink-2)' }}>#</th>
                      {headers.map(h => (
                        <th key={h} style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid var(--border)', fontWeight: 600, color: 'var(--ink-2)', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 10).map((row, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--bg)' }}>
                        <td style={{ padding: '7px 10px', color: 'var(--ink-3)' }}>{i + 2}</td>
                        {headers.map(h => (
                          <td key={h} style={{ padding: '7px 10px', color: 'var(--ink)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {h === 'tipo_dispositivo' && !DEVICE_TYPES.includes(row[h]) && row[h]
                              ? <span title={`"${row[h]}" → se usará "other"`} style={{ color: 'var(--amber)' }}>⚠ {row[h]}</span>
                              : (row[h] || <span style={{ color: 'var(--border-2)' }}>—</span>)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {rows.length > 10 && (
                <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 8, textAlign: 'center' }}>
                  ... y {rows.length - 10} filas más
                </div>
              )}
            </div>
          )}

          {/* PASO 3: Importing */}
          {step === 'importing' && (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <div className="spinner" style={{ margin: '0 auto 16px' }} />
              <div style={{ fontWeight: 600, color: 'var(--ink-2)' }}>Importando {rows.length} equipos...</div>
              <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 4 }}>Por favor esperá</div>
            </div>
          )}

          {/* PASO 4: Done */}
          {step === 'done' && result && (
            <div style={{ textAlign: 'center', padding: 32 }}>
              <div style={{ width: 64, height: 64, background: 'var(--green-dim)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                <Check size={32} style={{ color: 'var(--green)' }} />
              </div>
              <div style={{ fontWeight: 700, fontSize: 18, color: 'var(--ink)', marginBottom: 16 }}>¡Importación completada!</div>
              <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginBottom: 20 }}>
                <div style={{ background: 'var(--green-dim)', border: '1px solid #86efac', borderRadius: 10, padding: '12px 24px', textAlign: 'center' }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--green)' }}>{result.imported}</div>
                  <div style={{ fontSize: 12, color: 'var(--green)' }}>Nuevos activos</div>
                </div>
                <div style={{ background: 'var(--accent-dim)', border: '1px solid #93c5fd', borderRadius: 10, padding: '12px 24px', textAlign: 'center' }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--accent)' }}>{result.updated}</div>
                  <div style={{ fontSize: 12, color: 'var(--accent)' }}>Actualizados</div>
                </div>
                {result.errors?.length > 0 && (
                  <div style={{ background: 'var(--amber-dim)', border: '1px solid var(--amber-dim)', borderRadius: 10, padding: '12px 24px', textAlign: 'center' }}>
                    <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--amber)' }}>{result.errors.length}</div>
                    <div style={{ fontSize: 12, color: 'var(--amber)' }}>Con errores</div>
                  </div>
                )}
              </div>
              {result.errors?.length > 0 && (
                <div style={{ background: 'var(--amber-dim)', border: '1px solid var(--amber-dim)', borderRadius: 8, padding: 12, textAlign: 'left', maxHeight: 140, overflow: 'auto' }}>
                  {result.errors.map((e, i) => (
                    <div key={i} style={{ fontSize: 12, color: 'var(--amber)', marginBottom: 4 }}>
                      <strong>Fila {e.row}:</strong> {e.error}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button onClick={downloadTemplate} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 7, padding: '7px 14px', cursor: 'pointer', fontSize: 13, color: 'var(--ink-2)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Download size={14} /> Descargar plantilla
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            {step !== 'done' && (
              <button onClick={onClose} className="btn btn-secondary">Cancelar</button>
            )}
            {step === 'upload' && (
              <button onClick={() => fileRef.current.click()} className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Upload size={14} /> Seleccionar CSV
              </button>
            )}
            {step === 'preview' && (
              <>
                <button onClick={() => setStep('upload')} className="btn btn-secondary">← Cambiar archivo</button>
                <button onClick={doImport} className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Upload size={14} /> Importar {rows.length} equipos
                </button>
              </>
            )}
            {step === 'done' && (
              <button onClick={onClose} className="btn btn-primary">Listo</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const TYPE_MAP = {
  physical:  { label: 'Físico',     emoji: '🖥', color: 'var(--accent)', bg: '#E6F1FB' },
  virtual:   { label: 'Virtual',    emoji: '⬡',  color: 'var(--ink-2)', bg: 'var(--bg-subtle)' },
  cloud:     { label: 'Cloud',      emoji: '☁',  color: 'var(--ink-2)', bg: '#E0F7FA' },
  container: { label: 'Contenedor', emoji: '📦', color: 'var(--amber)', bg: 'var(--amber-dim)' },
};

const SUBTYPE_EMOJI = {
  workstation: '🖥', laptop: '💻', server: '🗄', printer: '🖨',
  switch: '🔀', router: '📡', access_point: '📶', monitor: '🖵',
  keyboard: '⌨', mouse: '🖱', ups: '🔋', phone: '📱',
  camera: '📷', scanner: '🖹', other: '📦',
};

function TypeBadge({ type, platform, subtype }) {
  const t = TYPE_MAP[type] || TYPE_MAP.physical;
  const emoji = (subtype && SUBTYPE_EMOJI[subtype]) ? SUBTYPE_EMOJI[subtype] : t.emoji;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <span style={{ color: t.color, fontSize: 11, fontWeight: 600 }}>
        {emoji} {t.label}
      </span>
      {platform && <span style={{ fontSize: 10, color: '#888' }}>{platform}</span>}
    </div>
  );
}

const CRITICIDAD_STYLE = {
  critica: { bg: 'var(--red-dim)', color: 'var(--red)', label: '🔴 Crítica' },
  alta:    { bg: 'var(--amber-dim)', color: '#D45D00', label: '🟠 Alta' },
  media:   { bg: '#FFFDE7', color: '#856404', label: '🟡 Media' },
  baja:    { bg: 'var(--green-dim)', color: 'var(--green)', label: '🟢 Baja' },
};

function CriticidadBadge({ value }) {
  const c = CRITICIDAD_STYLE[value];
  if (!c) return null;
  return (
    <span style={{ color: c.color, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
      {c.label}
    </span>
  );
}

const statusBadge = (s) => {
  const map = { active: ['badge-active', 'Al día'], warning: ['badge-warning', 'Pendiente'], critical: ['badge-critical', 'Crítico'], offline: ['badge-offline', 'Offline'] };
  const [cls, label] = map[s] || ['badge-offline', s];
  return <span className={`badge ${cls}`}>{label}</span>;
};

const GRADE_MAP = {
  secure:     { color: 'var(--green)', bg: 'var(--green-dim)', label: 'Seguro',    border: '#8DD5A0' },
  acceptable: { color: 'var(--accent)', bg: '#E6F1FB', label: 'Aceptable', border: '#90C4E8' },
  attention:  { color: '#D45D00', bg: 'var(--amber-dim)', label: 'Atencion',  border: '#F5C080' },
  critical:   { color: 'var(--red)', bg: 'var(--red-dim)', label: 'Critico',   border: '#F5C0BC' },
};

const LIFECYCLE_MAP = {
  active:         { label: 'Activo',        color: 'var(--green)', bg: 'var(--green-dim)' },
  decommissioned: { label: 'Dado de baja',  color: 'var(--ink-2)', bg: 'var(--bg)' },
  deleted:        { label: 'Eliminado',     color: 'var(--red)', bg: 'var(--red-dim)' },
  in_repair:      { label: 'En reparación', color: '#D45D00', bg: 'var(--amber-dim)' },
  in_stock:       { label: 'En depósito',   color: 'var(--accent)', bg: '#E6F1FB' },
  merged:         { label: 'Fusionado',     color: 'var(--ink-2)', bg: 'var(--bg-subtle)' },
  stolen:         { label: 'Robado',        color: 'var(--red)', bg: 'var(--red-dim)' },
};

function SecurityBadge({ score, grade }) {
  if (score == null) return <span style={{ fontSize: 11, color: '#aaa' }}>—</span>;
  const g = GRADE_MAP[grade] || GRADE_MAP.critical;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ width: 50, height: 4, background: '#eee', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${score}%`, background: g.color, borderRadius: 4 }} />
      </div>
      <span style={{ fontSize: 11, fontWeight: 600, color: g.color, minWidth: 24 }}>{score}</span>
      <span style={{ color: g.color, fontSize: 11, fontWeight: 600 }}>
        {g.label}
      </span>
    </div>
  );
}

function AssetCard({ a, timeSince }) {
  const ips = Array.isArray(a.ip_addresses) ? a.ip_addresses : (typeof a.ip_addresses === 'string' ? JSON.parse(a.ip_addresses || '[]') : []);
  const firstIp = ips[0]?.address || ips[0] || null;
  const g = GRADE_MAP[a.security_grade] || GRADE_MAP.critical;
  const lc = LIFECYCLE_MAP[a.lifecycle_status] || LIFECYCLE_MAP.active;
  const typeInfo = TYPE_MAP[a.asset_type || 'physical'] || TYPE_MAP.physical;
  const subtypeEmoji = (a.device_subtype && SUBTYPE_EMOJI[a.device_subtype]) ? SUBTYPE_EMOJI[a.device_subtype] : typeInfo.emoji;
  const initial = (a.hostname || '?')[0].toUpperCase();

  // Color del borde izquierdo según security grade
  const borderColor = a.security_score != null ? g.color : 'var(--border-2)';

  return (
    <Link
      to={`/assets/${a.id}`}
      style={{ textDecoration: 'none', color: 'inherit' }}
    >
      <div className="asset-card" style={{ borderLeftColor: borderColor }}>
        {/* Header de la card */}
        <div className="asset-card-header">
          <div className="asset-card-avatar" style={{ background: typeInfo.bg, color: typeInfo.color }}>
            {subtypeEmoji || initial}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="asset-card-hostname">{a.hostname}</div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 3 }}>
              <span style={{ color: typeInfo.color, fontSize: 11, fontWeight: 600 }}>
                {subtypeEmoji} {typeInfo.label}
              </span>
              <span style={{ color: lc.color, fontSize: 11, fontWeight: 600 }}>
                {lc.label}
              </span>
              {a.criticidad && <CriticidadBadge value={a.criticidad} />}
            </div>
          </div>
        </div>

        {/* Score de seguridad */}
        {a.security_score != null && (
          <div className="asset-card-score">
            <span style={{ fontSize: 11, color: 'var(--ink-2)' }}>Seguridad</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ flex: 1, height: 5, background: 'var(--border)', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${a.security_score}%`, background: g.color, borderRadius: 4 }} />
              </div>
              <span style={{ fontSize: 12, fontWeight: 700, color: g.color, minWidth: 26 }}>{a.security_score}</span>
              <span style={{ color: g.color, fontSize: 11, fontWeight: 600 }}>
                {g.label}
              </span>
            </div>
          </div>
        )}

        {/* Datos */}
        <div className="asset-card-meta">
          {a.os_name && (
            <div className="asset-card-row">
              <span className="asset-card-label">SO</span>
              <span className="asset-card-value">{a.os_name}</span>
            </div>
          )}
          {a.user_name && (
            <div className="asset-card-row">
              <User size={11} style={{ color: 'var(--ink-3)', flexShrink: 0 }} />
              <span className="asset-card-value">{a.user_name}</span>
            </div>
          )}
          {a.location_name && (
            <div className="asset-card-row">
              <MapPin size={11} style={{ color: 'var(--ink-3)', flexShrink: 0 }} />
              <span className="asset-card-value">{a.location_name}</span>
            </div>
          )}
          {firstIp && (
            <div className="asset-card-row">
              <span className="asset-card-label">IP</span>
              <span className="asset-card-value mono" style={{ fontSize: 11 }}>{firstIp}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="asset-card-footer">
          <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>{timeSince(a.last_seen)}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {statusBadge(a.status)}
            <span style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 600 }}>Ver →</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function Assets() {
  const navigate = useNavigate();
  const location = useLocation();
  const initialType      = new URLSearchParams(location.search).get('type')       || '';
  const initialAgentType = new URLSearchParams(location.search).get('agent_type') || '';

  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterGrade, setFilterGrade] = useState('');
  const [filterType, setFilterType] = useState(initialType);
  const [filterAgentType, setFilterAgentType] = useState(initialAgentType);
  const [filterLocation, setFilterLocation] = useState(() => localStorage.getItem('assets_filter_location') || '');
  const [locations, setLocations] = useState([]);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('assets_view') || 'table');
  const [showImport, setShowImport] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  // Sincronizar filtros con query params cuando cambia la URL
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    setFilterType(p.get('type') || '');
    setFilterAgentType(p.get('agent_type') || '');
  }, [location.search]);

  const setView = (mode) => {
    setViewMode(mode);
    localStorage.setItem('assets_view', mode);
  };

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (filterStatus) params.status = filterStatus;
    api.getAssets(params)
      .then(data => setAssets(Array.isArray(data) ? data : []))
      .finally(() => setLoading(false));
  }, [search, filterStatus]);

  useEffect(() => { load(); }, [load]);

  // Persistir el filtro de ubicación: si entrás a un equipo y volvés al inventario,
  // sigue filtrado por la última ubicación elegida (sobrevive al desmontaje).
  useEffect(() => {
    localStorage.setItem('assets_filter_location', filterLocation);
  }, [filterLocation]);

  // Cargar ubicaciones para el filtro
  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${API_URL}/locations`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setLocations(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  const timeSince = (dateStr) => {
    if (!dateStr) return '—';
    const diff = Date.now() - new Date(dateStr);
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'hace un momento';
    if (mins < 60) return `hace ${mins} min`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `hace ${hrs} h`;
    return `hace ${Math.floor(hrs / 24)} días`;
  };

  const filtered = assets.filter(a =>
    (!filterGrade     || a.security_grade === filterGrade) &&
    (!filterType      || (a.asset_type || 'physical') === filterType) &&
    (!filterAgentType || a.agent_type === filterAgentType) &&
    (!filterLocation  || a.location_name === filterLocation)
  );

  // ── Orden por columna (cliente). Primer clic en una columna = mayor a menor;
  // volver a clic invierte. Números (score) y fechas (última sync) ordenan numérico.
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('desc');
  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortKey(key); setSortDir('desc'); }
  };
  const sortArrow = (key) => (sortKey === key ? (sortDir === 'desc' ? ' ↓' : ' ↑') : '');
  const thSort = { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };
  const sortVal = (a, key) => {
    switch (key) {
      case 'hostname':       return (a.hostname || '').toLowerCase();
      case 'asset_type':     return (a.asset_type || 'physical').toLowerCase();
      case 'user_name':      return (a.user_name || '').toLowerCase();
      case 'location_name':  return (a.location_name || '').toLowerCase();
      case 'os_name':        return (a.os_name || '').toLowerCase();
      case 'status':         return (a.status || '').toLowerCase();
      case 'security_score': return a.security_score == null ? -1 : Number(a.security_score);
      case 'last_seen':      return a.last_seen ? new Date(a.last_seen).getTime() : 0;
      case 'ip': {
        let ips = [];
        try { ips = Array.isArray(a.ip_addresses) ? a.ip_addresses
                    : (typeof a.ip_addresses === 'string' ? JSON.parse(a.ip_addresses || '[]') : []); } catch { ips = []; }
        return String(ips[0]?.address || ips[0] || '');
      }
      default: return '';
    }
  };
  const sorted = React.useMemo(() => {
    if (!sortKey) return filtered;
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((x, y) => {
      const vx = sortVal(x, sortKey), vy = sortVal(y, sortKey);
      if (typeof vx === 'number' && typeof vy === 'number') return (vx - vy) * dir;
      return String(vx).localeCompare(String(vy), 'es', { numeric: true }) * dir;
    });
  }, [filtered, sortKey, sortDir]);

  const getRowBg = (grade) => {
    if (grade === 'critical') return 'rgba(192,57,43,0.04)';
    if (grade === 'attention') return 'rgba(212,93,0,0.03)';
    return 'transparent';
  };

  // Selección masiva — solo Windows con agente standard
  const windowsAgentAssets = filtered.filter(a =>
    a.agent_standard && (a.os_name || '').toLowerCase().includes('windows')
  );
  const allSelected = windowsAgentAssets.length > 0 && windowsAgentAssets.every(a => selected.has(a.id));

  const toggleAll = () => {
    if (allSelected) {
      setSelected(s => { const n = new Set(s); windowsAgentAssets.forEach(a => n.delete(a.id)); return n; });
    } else {
      setSelected(s => { const n = new Set(s); windowsAgentAssets.forEach(a => n.add(a.id)); return n; });
    }
  };

  const toggleOne = (id) => {
    setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const sendBulkWindowsUpdate = async () => {
    if (!selected.size) return;
    setBulkSending(true); setBulkResult(null);
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
    let ok = 0, fail = 0;
    await Promise.all([...selected].map(async id => {
      try {
        const r = await fetch(`${API_URL}/assets/${id}/actions`, {
          method: 'POST', headers,
          body: JSON.stringify({ action_type: 'windows_update' }),
        });
        r.ok ? ok++ : fail++;
      } catch { fail++; }
    }));
    setBulkSending(false);
    setBulkResult({ ok, fail });
    setSelected(new Set());
  };

  return (
    <div className="assets-page">
      <div className="page-header">
        <div>
          <div className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Monitor size={22} style={{ opacity: 0.7 }} />
            {filterAgentType === 'android' ? 'Móviles' : ({ physical: 'Activos físicos', virtual: 'Activos virtuales', cloud: 'Activos cloud', container: 'Contenedores' }[filterType] || 'Inventario de activos')}
          </div>
          <div className="page-sub">Configuration Management Database (CMDB)</div>
        </div>
        {/* contador + buscador + acciones */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div className="assets-count">{filtered.length} equipos</div>
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#aaa', pointerEvents: 'none' }} />
            <input
              className="form-input search-input"
              placeholder="Buscar hostname o usuario..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ paddingLeft: 32, width: 220 }}
            />
          </div>
          <button className="btn btn-secondary" onClick={load} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <RefreshCw size={14} /> Actualizar
          </button>
          <button className="btn btn-secondary" onClick={() => setShowImport(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <Upload size={14} /> Importar CSV
          </button>
          <button className="btn btn-primary" onClick={() => navigate('/assets/new')} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            + Nuevo activo
          </button>
        </div>
      </div>

      <div className="card">
        {/* ── Fila única: toggle vista + filtros ── */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          {/* Toggle lista / tarjetas — a la izquierda */}
          <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', marginRight: 4 }}>
            <button onClick={() => setView('table')} title="Vista tabla"
              style={{ padding: '6px 10px', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', background: viewMode === 'table' ? 'var(--accent)' : '#fff', color: viewMode === 'table' ? '#fff' : 'var(--ink-2)' }}>
              <List size={15} />
            </button>
            <button onClick={() => setView('cards')} title="Vista cards"
              style={{ padding: '6px 10px', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', background: viewMode === 'cards' ? 'var(--accent)' : '#fff', color: viewMode === 'cards' ? '#fff' : 'var(--ink-2)', borderLeft: '1px solid var(--border)' }}>
              <LayoutGrid size={15} />
            </button>
          </div>
          {/* Filtros a la derecha del toggle */}
          <select className="form-input filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">Todos los estados</option>
            <option value="active">Al día</option>
            <option value="warning">Parche pendiente</option>
            <option value="critical">Crítico</option>
            <option value="offline">Offline</option>
          </select>
          <select className="form-input filter-select" value={filterType} onChange={e => setFilterType(e.target.value)}>
            <option value="">Todos los tipos</option>
            <option value="physical">Físico</option>
            <option value="virtual">Virtual</option>
            <option value="cloud">Cloud</option>
            <option value="container">Contenedor</option>
          </select>
          <select className="form-input filter-select" value={filterGrade} onChange={e => setFilterGrade(e.target.value)}>
            <option value="">Todos los niveles</option>
            <option value="secure">Seguro</option>
            <option value="acceptable">Aceptable</option>
            <option value="attention">Atención</option>
            <option value="critical">Crítico</option>
          </select>
          {locations.length > 0 && (
            <select className="form-input filter-select" value={filterLocation} onChange={e => setFilterLocation(e.target.value)}>
              <option value="">Todas las ubicaciones</option>
              {locations.map(l => (
                <option key={l.id} value={l.name}>{l.name}</option>
              ))}
            </select>
          )}
        </div>

        {/* ── Barra de acciones masivas ── */}
        {selected.size > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', background: 'var(--accent-dim)', border: '1px solid #bfdbfe', borderRadius: 8, marginBottom: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent)' }}>
              {selected.size} equipo{selected.size > 1 ? 's' : ''} seleccionado{selected.size > 1 ? 's' : ''}
            </span>
            <button className="btn btn-primary" onClick={sendBulkWindowsUpdate} disabled={bulkSending}
              style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <i className="ti ti-refresh" style={{ fontSize: 14 }} />
              {bulkSending ? 'Enviando...' : '🪟 Actualizar Windows'}
            </button>
            <button className="btn btn-secondary" onClick={() => setSelected(new Set())}
              style={{ fontSize: 12 }}>
              Cancelar selección
            </button>
          </div>
        )}
        {bulkResult && (
          <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 12, fontSize: 13,
            background: bulkResult.fail === 0 ? 'var(--green-dim)' : 'var(--amber-dim)',
            color: bulkResult.fail === 0 ? 'var(--green)' : 'var(--amber)',
            border: `1px solid ${bulkResult.fail === 0 ? '#86efac' : '#fde047'}` }}>
            ✅ {bulkResult.ok} acción{bulkResult.ok !== 1 ? 'es' : ''} enviada{bulkResult.ok !== 1 ? 's' : ''} correctamente.
            {bulkResult.fail > 0 && ` ⚠️ ${bulkResult.fail} fallaron.`}
            {' '}Las actualizaciones se ejecutarán en el próximo ciclo del agente.
            <button onClick={() => setBulkResult(null)} style={{ marginLeft: 12, background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, opacity: 0.6 }}>✕</button>
          </div>
        )}

        {/* ── VISTA TABLA ── */}
        {viewMode === 'table' && (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 36, textAlign: 'center' }}>
                    <input type="checkbox" checked={allSelected} onChange={toggleAll}
                      title="Seleccionar todos los Windows con agente"
                      style={{ cursor: 'pointer' }} />
                  </th>
                  <th onClick={() => toggleSort('hostname')} style={thSort}>Hostname{sortArrow('hostname')}</th>
                  <th onClick={() => toggleSort('asset_type')} style={thSort}>Tipo{sortArrow('asset_type')}</th>
                  <th onClick={() => toggleSort('user_name')} style={thSort}>Usuario{sortArrow('user_name')}</th>
                  <th onClick={() => toggleSort('location_name')} style={thSort}>Ubicación{sortArrow('location_name')}</th>
                  <th onClick={() => toggleSort('os_name')} style={thSort}>Sistema operativo{sortArrow('os_name')}</th>
                  <th onClick={() => toggleSort('ip')} style={thSort}>IP principal{sortArrow('ip')}</th>
                  <th onClick={() => toggleSort('status')} style={thSort}>Estado{sortArrow('status')}</th>
                  <th onClick={() => toggleSort('security_score')} style={{ ...thSort, display: 'flex', alignItems: 'center', gap: 4 }}><Shield size={12} /> Seguridad{sortArrow('security_score')}</th>
                  <th onClick={() => toggleSort('last_seen')} style={thSort}>Última sync{sortArrow('last_seen')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={11} style={{ textAlign: 'center', padding: '32px' }}>
                    <div className="spinner" style={{ margin: '0 auto' }} />
                  </td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={11} className="empty-row">
                    Sin resultados. {search || filterStatus || filterGrade || filterType ? 'Probá con otros filtros.' : 'Ejecutá el agente en un equipo para registrar activos.'}
                  </td></tr>
                ) : sorted.map(a => {
                  const ips = Array.isArray(a.ip_addresses) ? a.ip_addresses : (typeof a.ip_addresses === 'string' ? JSON.parse(a.ip_addresses || '[]') : []);
                  const firstIp = ips[0]?.address || ips[0] || '—';
                  return (
                    <tr key={a.id} style={{ background: selected.has(a.id) ? 'var(--accent-dim)' : getRowBg(a.security_grade) }}>
                      <td style={{ textAlign: 'center' }}>
                        {a.agent_standard && (a.os_name || '').toLowerCase().includes('windows') ? (
                          <input type="checkbox" checked={selected.has(a.id)} onChange={() => toggleOne(a.id)}
                            style={{ cursor: 'pointer' }} />
                        ) : null}
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Cpu size={14} style={{ opacity: 0.4, flexShrink: 0 }} />
                          <Link to={`/assets/${a.id}`} className="hostname-link mono">{a.hostname}</Link>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                          <TypeBadge type={a.asset_type || 'physical'} platform={a.virtual_platform} subtype={a.device_subtype} />
                          {a.criticidad && <CriticidadBadge value={a.criticidad} />}
                        </div>
                      </td>
                      <td>{a.user_name || '—'}</td>
                      <td>{a.location_name || '—'}</td>
                      <td>
                        <div style={{ fontSize: 13 }}>{a.os_name || '—'}</div>
                        <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{a.patch_level}</div>
                      </td>
                      <td><span className="mono">{firstIp}</span></td>
                      <td>{statusBadge(a.status)}</td>
                      <td><SecurityBadge score={a.security_score} grade={a.security_grade} /></td>
                      <td style={{ color: 'var(--gray-500)', fontSize: 12 }}>{timeSince(a.last_seen)}</td>
                      <td>
                        <Link to={`/assets/${a.id}`} className="btn btn-secondary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          Ver <ChevronRight size={12} />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* ── VISTA CARDS ── */}
        {viewMode === 'cards' && (
          <div style={{ padding: '16px 0' }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: 48 }}>
                <div className="spinner" style={{ margin: '0 auto' }} />
              </div>
            ) : filtered.length === 0 ? (
              <div className="empty-row" style={{ padding: 48, textAlign: 'center' }}>
                Sin resultados. {search || filterStatus || filterGrade || filterType ? 'Probá con otros filtros.' : 'Ejecutá el agente en un equipo para registrar activos.'}
              </div>
            ) : (
              <div className="assets-grid">
                {filtered.map(a => (
                  <AssetCard key={a.id} a={a} timeSince={timeSince} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {showImport && (
        <ImportModal
          onClose={() => setShowImport(false)}
          onImported={() => { load(); }}
        />
      )}
    </div>
  );
}
